<template>
  <ManagerLayout>
    <div class="manager-shikoku">
      <h2 class="page-title">四国こども英語チャレンジ 追加者一覧</h2>
      <p class="page-desc">
        四国の公式LINEを友だち追加した人を、追加した順に1人1行で表示します。
        ミニレッスン・フィードバック・「続けたい？」の回答まで、どこまで進んだかが分かります。
      </p>

      <section class="manager-section">
        <div class="summary-grid">
          <button
            v-for="s in stageDefs"
            :key="s.key"
            type="button"
            class="summary-card"
            :class="{ active: stageFilter === s.key, alert: s.alert && (summary[s.key] || 0) > 0 }"
            @click="stageFilter = stageFilter === s.key ? '' : s.key"
          >
            <span class="summary-count">{{ summary[s.key] || 0 }}</span>
            <span class="summary-label">{{ s.label }}</span>
          </button>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <h3 class="card-title">追加案内を1通だけ送る（旧い文面を送った人向け・1人1回だけ）</h3>
          <p class="card-desc">
            追撃LINE（①②）を旧い文面で送ってしまい、まだ提出していない人に、新しい文面（見本音声つき）を1通だけ送ります。
            ページを開いている人には②の文面、開いていない人には①の文面を送ります。
          </p>
          <button type="button" class="btn btn-secondary" :disabled="catchupLoading" @click="loadCatchup">
            {{ catchupLoading ? '読み込み中...' : '対象を表示する' }}
          </button>
          <p v-if="catchupMessage" class="catchup-message">{{ catchupMessage }}</p>
          <template v-if="catchupLoaded">
            <p v-if="!catchup.length" class="total-note">送る対象の人はいません。</p>
            <div v-else class="table-wrap">
              <table class="data-table" aria-label="追加案内の送り先">
                <thead>
                  <tr><th class="center">送る</th><th>追加日時（JST）</th><th>LINE名</th><th>送る文面</th></tr>
                </thead>
                <tbody>
                  <tr v-for="c in catchup" :key="c.lineUserId">
                    <td class="center"><input v-model="catchupSelected" type="checkbox" :value="c.lineUserId" /></td>
                    <td>{{ formatDateTimeJst(c.followedAt) }}</td>
                    <td>{{ c.lineDisplayName || '（取得できません）' }}</td>
                    <td>{{ stageMark(c.messageStage) }}の新文面（{{ c.pageReached ? 'ページ表示済み' : 'ページ未表示' }}）</td>
                  </tr>
                </tbody>
              </table>
              <button type="button" class="btn btn-primary" :disabled="catchupSending || !catchupSelected.length" @click="sendCatchup">
                {{ catchupSending ? '送信中...' : `選んだ ${catchupSelected.length} 人に1通だけ送る` }}
              </button>
            </div>
          </template>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <h3 class="card-title">10秒・伸びしろ診断のご案内を1通だけ送る（すでに友だちの人向け・1人1回だけ）</h3>
          <p class="card-desc">
            四国のLINEの中で、先生が声を聞いて返す10秒の診断（英文を2つ読んで、トークの右下のマイクから音声を送る）をご案内します。
            送ると、説明と「診断を始める」ボタンが届きます。ミニレッスンの時代に友だち追加した人で、まだ提出していない人が対象です。
            文面に「今治市内でお申し込みいただいた生徒さん」の文が入っています。<strong>今治市の人だけ</strong>を選んでください（最初は誰も選ばれていません）。
          </p>
          <button type="button" class="btn btn-secondary" :disabled="voiceInviteLoading" @click="loadVoiceInvite">
            {{ voiceInviteLoading ? '読み込み中...' : '対象を表示する' }}
          </button>
          <p v-if="voiceInviteMessage" class="catchup-message">{{ voiceInviteMessage }}</p>
          <template v-if="voiceInviteLoaded">
            <p v-if="!voiceInvite.length" class="total-note">送る対象の人はいません。</p>
            <div v-else class="table-wrap">
              <button type="button" class="btn btn-secondary" @click="toggleVoiceInviteAll">
                {{ voiceInviteSelected.length === voiceInvite.length ? 'すべて外す' : 'すべて選ぶ' }}
              </button>
              <table class="data-table" aria-label="10秒診断のご案内の送り先">
                <thead>
                  <tr><th class="center">送る</th><th>追加日時（JST）</th><th>LINE名</th></tr>
                </thead>
                <tbody>
                  <tr v-for="c in voiceInvite" :key="c.lineUserId">
                    <td class="center"><input v-model="voiceInviteSelected" type="checkbox" :value="c.lineUserId" /></td>
                    <td>{{ formatDateTimeJst(c.followedAt) }}</td>
                    <td>{{ c.lineDisplayName || '（取得できません）' }}<span v-if="c.isTest">（テスト用・何度でも送れます）</span></td>
                  </tr>
                </tbody>
              </table>
              <button type="button" class="btn btn-primary" :disabled="voiceInviteSending || !voiceInviteSelected.length" @click="sendVoiceInvite">
                {{ voiceInviteSending ? '送信中...' : `選んだ ${voiceInviteSelected.length} 人に1通だけ送る` }}
              </button>
            </div>
          </template>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <h3 class="card-title">「やってみたい」を選んだ人へ、オトエイゴへの再案内を1通だけ送る（1人1回だけ）</h3>
          <p class="card-desc">
            「①やってみたい」を押したのに、姉妹サービス「オトエイゴ」のLINE追加・登録に進んでいない人に、もう一度ご案内します。
            四国のLINEとオトエイゴのLINEは別アカウントのため、登録に進んだかどうかは自動では分かりません。
            <strong>すでに練習を始めている人を選ばないよう、LINE名を見て、送る人だけを選んでください</strong>（最初は誰も選ばれていません）。
          </p>
          <button type="button" class="btn btn-secondary" :disabled="reminderLoading" @click="loadReminder">
            {{ reminderLoading ? '読み込み中...' : '対象を表示する' }}
          </button>
          <p v-if="reminderMessage" class="catchup-message">{{ reminderMessage }}</p>
          <template v-if="reminderLoaded">
            <p v-if="!reminder.length" class="total-note">送る対象の人はいません。</p>
            <div v-else class="table-wrap">
              <table class="data-table" aria-label="再案内の送り先">
                <thead>
                  <tr><th class="center">送る</th><th>「やってみたい」を押した日時（JST）</th><th>LINE名</th></tr>
                </thead>
                <tbody>
                  <tr v-for="c in reminder" :key="c.lineUserId">
                    <td class="center"><input v-model="reminderSelected" type="checkbox" :value="c.lineUserId" /></td>
                    <td>{{ formatDateTimeJst(c.yesAt) }}</td>
                    <td>{{ c.lineDisplayName || '（取得できません）' }}</td>
                  </tr>
                </tbody>
              </table>
              <button type="button" class="btn btn-primary" :disabled="reminderSending || !reminderSelected.length" @click="sendReminder">
                {{ reminderSending ? '送信中...' : `選んだ ${reminderSelected.length} 人に1通だけ送る` }}
              </button>
            </div>
          </template>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">期間</span>
              <select v-model="days" class="form-input" @change="load">
                <option value="7">直近7日</option>
                <option value="30">直近30日</option>
                <option value="90">直近90日</option>
              </select>
            </label>
            <label class="filter-item filter-search">
              <span class="filter-label">検索</span>
              <input v-model="searchQ" type="text" placeholder="LINE名・お子さまのお名前" class="form-input" />
            </label>
            <button type="button" class="btn btn-secondary" :disabled="loading" @click="load">
              {{ loading ? '読み込み中...' : '更新' }}
            </button>
          </div>
          <p class="total-note">
            全 {{ total }} 人（追加が新しい順）<span v-if="stageFilter">／絞り込み：{{ stageLabel(stageFilter) }}（{{ visibleRows.length }}人）</span>
          </p>
          <p v-if="!nudgeTableReady" class="notice-warn">
            追撃LINEの送信記録テーブルがまだ作られていないため、「追撃LINE」の列は空です。
          </p>
          <div v-if="errorMessage" class="error-state">{{ errorMessage }}</div>
          <div v-else-if="loading && !rows.length" class="loading-state">読み込み中...</div>
          <div v-else-if="!visibleRows.length" class="empty-state">該当する人がいません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="四国 友だち追加者一覧">
              <thead>
                <tr>
                  <th>追加日時（JST）</th>
                  <th>LINE名</th>
                  <th>お子さまのお名前</th>
                  <th>いまの状態</th>
                  <th class="center">ページ<br />表示</th>
                  <th class="center">録音<br />開始</th>
                  <th class="center">提出</th>
                  <th class="center">FB<br />送付</th>
                  <th class="center">FB<br />視聴</th>
                  <th>続けたい？</th>
                  <th>追撃LINE</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in visibleRows" :key="r.lineUserId" class="data-row">
                  <td>{{ formatDateTimeJst(r.followedAt) }}</td>
                  <td>{{ r.lineDisplayName || '（取得できません）' }}</td>
                  <td>{{ r.childName || '—' }}</td>
                  <td>
                    <span class="stage-badge" :class="'stage-' + r.stage">{{ stageLabel(r.stage) }}</span>
                    <span v-if="r.needsFollowUp" class="follow-flag">24時間以上 未開始</span>
                  </td>
                  <td v-for="k in checkKeys" :key="k" class="center" :title="r[k] ? formatDateTimeJst(r[k]) : '未到達'">
                    <span v-if="r[k]" class="check-mark">✅</span>
                    <span v-else class="check-empty">—</span>
                  </td>
                  <td>
                    <template v-if="r.continueYesAt">①やってみたい</template>
                    <template v-else-if="r.continueNoAt">②やめておく<span v-if="r.declineReason">（{{ r.declineReason }}）</span></template>
                    <template v-else>—</template>
                  </td>
                  <td>
                    <span
                      v-for="n in r.nudges"
                      :key="n.stage"
                      class="nudge-chip"
                      :class="'nudge-' + n.status"
                      :title="n.error || ''"
                    >{{ stageMark(n.stage) }}{{ nudgeStatusLabel(n.status) }} {{ n.status === 'sent' ? formatDateTimeJst(n.at) : '' }}</span>
                    <span v-if="r.nextNudge" class="nudge-chip nudge-planned">
                      {{ stageMark(r.nextNudge.stage) }}予定 {{ formatDateTimeJst(r.nextNudge.dueAt) }}
                    </span>
                    <span v-if="!r.nudges.length && !r.nextNudge" class="check-empty">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

// 状態の定義（進み具合の順）。alert は「こちらの対応が必要」な状態
const stageDefs = [
  { key: 'followed', label: '追加のみ（未開始）' },
  { key: 'page_reached', label: 'ページを開いた' },
  { key: 'started', label: '録音を始めた' },
  { key: 'waiting_feedback', label: 'フィードバック待ち', alert: true },
  { key: 'feedback_sent', label: 'FB送付済み' },
  { key: 'feedback_viewed', label: 'FB視聴済み' },
  { key: 'continue_yes', label: '①やってみたい' },
  { key: 'continue_no', label: '②やめておく' }
]
const checkKeys = ['pageReachedAt', 'startedAt', 'submittedAt', 'feedbackSentAt', 'feedbackViewedAt']

const days = ref('30')
const searchQ = ref('')
const stageFilter = ref('')
const rows = ref([])
const summary = ref({})
const total = ref(0)
const catchup = ref([])
const catchupSelected = ref([])
const catchupLoaded = ref(false)
const catchupLoading = ref(false)
const catchupSending = ref(false)
const catchupMessage = ref('')
const voiceInvite = ref([])
const voiceInviteSelected = ref([])
const voiceInviteLoaded = ref(false)
const voiceInviteLoading = ref(false)
const voiceInviteSending = ref(false)
const voiceInviteMessage = ref('')
const reminder = ref([])
const reminderSelected = ref([])
const reminderLoaded = ref(false)
const reminderLoading = ref(false)
const reminderSending = ref(false)
const reminderMessage = ref('')
const nudgeTableReady = ref(true)
const loading = ref(false)
const errorMessage = ref('')

const stageMark = (stage) => ({ 1: '①', 2: '②', 3: '③', 4: '追加案内', 5: '診断案内', 6: '再案内' }[stage] || '')
const nudgeStatusLabel = (status) => ({ sent: '送信済', sending: '送信中', failed: '失敗' }[status] || status)

const stageLabel = (key) => stageDefs.find((s) => s.key === key)?.label || key

const visibleRows = computed(() => {
  const term = searchQ.value.trim().toLowerCase()
  return rows.value.filter((r) => {
    if (stageFilter.value && r.stage !== stageFilter.value) return false
    if (!term) return true
    return `${r.lineDisplayName || ''} ${r.childName || ''}`.toLowerCase().includes(term)
  })
})

const formatDateTimeJst = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

const load = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${apiBase}/api/manager/shikoku-funnel?days=${encodeURIComponent(days.value)}`, {
      headers: { Authorization: `Bearer ${session?.access_token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      errorMessage.value = data.error || '一覧を取得できませんでした'
      rows.value = []
      summary.value = {}
      total.value = 0
      return
    }
    rows.value = data.rows || []
    summary.value = data.summary || {}
    total.value = data.total || 0
    nudgeTableReady.value = data.nudgeTableReady !== false
  } catch (_) {
    errorMessage.value = '一覧を取得できませんでした'
  } finally {
    loading.value = false
  }
}

const authHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' }
}

const loadCatchup = async () => {
  catchupLoading.value = true
  catchupMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-catchup`, { headers: await authHeaders() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      catchupMessage.value = data.error || '対象を取得できませんでした'
      return
    }
    catchup.value = data.candidates || []
    catchupSelected.value = catchup.value.map((c) => c.lineUserId)
    catchupLoaded.value = true
  } catch (_) {
    catchupMessage.value = '対象を取得できませんでした'
  } finally {
    catchupLoading.value = false
  }
}

const sendCatchup = async () => {
  const n = catchupSelected.value.length
  if (!n) return
  if (!window.confirm(`${n}人に、新しい文面を1通ずつ送ります。送ったあとは取り消せません。よろしいですか？`)) return
  catchupSending.value = true
  catchupMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-catchup/send`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ lineUserIds: catchupSelected.value })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      catchupMessage.value = data.error || '送信に失敗しました'
      return
    }
    catchupMessage.value = `送信しました：成功 ${data.sent} 人／失敗 ${data.failed} 人／対象外 ${data.skipped} 人`
    await Promise.all([loadCatchup(), load()])
  } catch (_) {
    catchupMessage.value = '送信に失敗しました'
  } finally {
    catchupSending.value = false
  }
}

const loadVoiceInvite = async () => {
  voiceInviteLoading.value = true
  voiceInviteMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-voice-invite`, { headers: await authHeaders() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      voiceInviteMessage.value = data.error || '対象を取得できませんでした'
      return
    }
    voiceInvite.value = data.candidates || []
    // 文面に「今治市内」の文があるため、最初は誰も選ばない（今治市の人だけを手で選ぶ）
    voiceInviteSelected.value = []
    voiceInviteLoaded.value = true
  } catch (_) {
    voiceInviteMessage.value = '対象を取得できませんでした'
  } finally {
    voiceInviteLoading.value = false
  }
}

const toggleVoiceInviteAll = () => {
  voiceInviteSelected.value = voiceInviteSelected.value.length === voiceInvite.value.length
    ? []
    : voiceInvite.value.map((c) => c.lineUserId)
}

const sendVoiceInvite = async () => {
  const n = voiceInviteSelected.value.length
  if (!n) return
  if (!window.confirm(`${n}人に、10秒診断のご案内を1通ずつ送ります。文面には「今治市内」の文が入っています。送ったあとは取り消せません。よろしいですか？`)) return
  voiceInviteSending.value = true
  voiceInviteMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-voice-invite/send`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ lineUserIds: voiceInviteSelected.value })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      voiceInviteMessage.value = data.error || '送信に失敗しました'
      return
    }
    voiceInviteMessage.value = `送信しました：成功 ${data.sent} 人／失敗 ${data.failed} 人／対象外 ${data.skipped} 人`
    await Promise.all([loadVoiceInvite(), load()])
  } catch (_) {
    voiceInviteMessage.value = '送信に失敗しました'
  } finally {
    voiceInviteSending.value = false
  }
}

const loadReminder = async () => {
  reminderLoading.value = true
  reminderMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-continue-reminder`, { headers: await authHeaders() })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      reminderMessage.value = data.error || '対象を取得できませんでした'
      return
    }
    reminder.value = data.candidates || []
    // すでに練習を始めている人を誤って選ばないよう、最初は誰も選ばない
    reminderSelected.value = []
    reminderLoaded.value = true
  } catch (_) {
    reminderMessage.value = '対象を取得できませんでした'
  } finally {
    reminderLoading.value = false
  }
}

const sendReminder = async () => {
  const n = reminderSelected.value.length
  if (!n) return
  if (!window.confirm(`${n}人に、オトエイゴへの再案内を1通ずつ送ります。すでに練習を始めている人が含まれていないことを確認してください。送ったあとは取り消せません。よろしいですか？`)) return
  reminderSending.value = true
  reminderMessage.value = ''
  try {
    const res = await fetch(`${apiBase}/api/manager/shikoku-continue-reminder/send`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ lineUserIds: reminderSelected.value })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      reminderMessage.value = data.error || '送信に失敗しました'
      return
    }
    reminderMessage.value = `送信しました：成功 ${data.sent} 人／失敗 ${data.failed} 人／対象外 ${data.skipped} 人`
    await Promise.all([loadReminder(), load()])
  } catch (_) {
    reminderMessage.value = '送信に失敗しました'
  } finally {
    reminderSending.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.manager-shikoku { padding: 0; }
.page-title { margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #1e293b; }
.page-desc { margin: 0 0 1.5rem 0; color: #64748b; font-size: 0.9rem; }
.manager-section { margin-bottom: 1.5rem; }
.card { background: white; border-radius: 8px; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }

.summary-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 0.75rem; }
.summary-card {
  display: flex; flex-direction: column; align-items: flex-start; gap: 0.25rem;
  padding: 0.75rem 1rem; background: white; border: 1px solid #e2e8f0; border-radius: 8px;
  cursor: pointer; text-align: left;
}
.summary-card.active { border-color: #3b82f6; box-shadow: 0 0 0 2px #bfdbfe; }
.summary-card.alert { border-color: #f59e0b; background: #fffbeb; }
.summary-count { font-size: 1.6rem; font-weight: 700; color: #1e293b; line-height: 1; }
.summary-label { font-size: 0.8rem; color: #64748b; }

.filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem; margin-bottom: 0.75rem; }
.filter-item { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-label { font-size: 0.85rem; color: #64748b; font-weight: 500; }
.filter-search .form-input { min-width: 220px; }
.form-input { padding: 0.4rem 0.6rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.9rem; }
.btn { padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer; border: 1px solid transparent; }
.btn-secondary { background: #e2e8f0; color: #334155; border-color: #cbd5e1; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.total-note { margin: 0 0 0.75rem 0; font-size: 0.9rem; color: #64748b; }

.loading-state, .empty-state, .error-state { padding: 2rem; text-align: center; color: #64748b; }
.error-state { color: #b91c1c; }
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.data-table th, .data-table td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.82rem; line-height: 1.3; }
.data-table th.center, .data-table td.center { text-align: center; }
.check-mark { color: #16a34a; }
.check-empty { color: #cbd5e1; }

.stage-badge { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.78rem; background: #f1f5f9; color: #475569; }
.stage-waiting_feedback { background: #fef3c7; color: #92400e; font-weight: 600; }
.stage-continue_yes { background: #dcfce7; color: #166534; }
.stage-continue_no { background: #fee2e2; color: #991b1b; }
.nudge-chip { display: inline-block; margin-right: 0.35rem; padding: 0.1rem 0.45rem; border-radius: 6px; font-size: 0.75rem; background: #f1f5f9; color: #475569; }
.nudge-sent { background: #dcfce7; color: #166534; }
.nudge-sending { background: #e0f2fe; color: #075985; }
.nudge-failed { background: #fee2e2; color: #991b1b; font-weight: 600; }
.nudge-planned { background: #fff7ed; color: #9a3412; }
.notice-warn { margin: 0 0 0.75rem 0; padding: 0.5rem 0.75rem; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; color: #92400e; font-size: 0.85rem; }
.card-title { margin: 0 0 0.5rem 0; font-size: 1rem; color: #1e293b; }
.card-desc { margin: 0 0 0.75rem 0; color: #64748b; font-size: 0.85rem; }
.catchup-message { margin: 0.75rem 0; font-size: 0.9rem; color: #166534; }
.btn-primary { background: #3b82f6; color: white; border-color: #3b82f6; margin-top: 0.75rem; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.follow-flag { margin-left: 0.4rem; font-size: 0.75rem; color: #b45309; }
</style>
